import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { appDataDetails } from '../../../../../../../../../../shared/core/shared.selectors';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { Ability, subject } from '@casl/ability';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { DailyReportService } from '../../../../daily-report.service';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-cle-contractor-form',
  templateUrl: './cle-contractor-form.component.html',
  styleUrls: ['./cle-contractor-form.component.scss'],
})
export class CleContractorFormComponent implements OnInit, OnDestroy {
  editable = false;
  @Input() data: any;

  contractorFormFields: any[] = [];
  subContractorOptions = [];
  form = new FormGroup<any>({
    sub_contractor: new FormControl(null),
    pictures: new FormControl('[]'),
    videos: new FormControl('[]'),
    comment: new FormControl(null),
  });
  contractorName: string = '';
  mediaFiles: any[] = [];
  showComment = false;
  errorMessages: any = {};
  formSubscription: Subscription = new Subscription();
  private fieldsInitialized = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private dailyReportService: DailyReportService,
    private commonService: CommonService,
    private ability: Ability,
  ) {
  }

  ngOnInit() {
    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        this.editable = this.ability.can('create', subject('DailyReport', { ...report }));
      });

    this.contractorName = this.data?.sub_contractor?.name || '';

    if (!this.data) {
      this.data = {
        id: `new_${new Date().getTime()}`,
        sub_contractor: null,
        field_values: [],
        pictures: [],
        videos: [],
        comment: null,
      };
      this.dailyReportService.disableOptionMenu.next(true);
    }

    if (this.data?.pictures?.length || this.data?.videos?.length) {
      this.mediaFiles = [...(this.data.pictures || []), ...(this.data.videos || [])];
      this.form.get('pictures')?.setValue(JSON.stringify(this.data.pictures || []), { emitEvent: false });
      this.form.get('videos')?.setValue(JSON.stringify(this.data.videos || []), { emitEvent: false });
    }

    if (this.data?.comment) {
      this.showComment = true;
      this.form.get('comment')?.setValue(this.data.comment, { emitEvent: false });
    }

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.subContractorOptions = project?.sub_contractors || [];
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const fields = data?.dailyReportFields?.sub_contractor?.fields || [];
        this.contractorFormFields = [...fields].sort((a, b) => a.display_order - b.display_order);
        this.initFormControls();
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.validate()) {
          this.save();
        }
      });

    this.dailyReportService.triggerDelete
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.delete();
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];
        items.forEach((eachItem: any) => {
          if (eachItem?.fileType === 'picture' || eachItem?.fileType === 'document') {
            let pictures = JSON.parse(this.form.get('pictures')?.value || '[]');
            pictures.push(eachItem);
            this.form.get('pictures')?.setValue(JSON.stringify(pictures));
            this.mediaFiles.push(eachItem);
          } else if (eachItem?.fileType === 'video') {
            let videos = JSON.parse(this.form.get('videos')?.value || '[]');
            videos.push(eachItem);
            this.form.get('videos')?.setValue(JSON.stringify(videos));
            this.mediaFiles.push(eachItem);
          }
        });
        this.mediaFiles = [...this.mediaFiles];
      });

    this.dailyReportService.addComment
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.showComment = true;
      });

  }

  private initFormControls() {
    if (this.fieldsInitialized) return;
    this.fieldsInitialized = true;

    if (this.data?.sub_contractor?.id) {
      this.form.controls['sub_contractor'].setValue(this.data.sub_contractor.id, { emitEvent: false });
    }

    this.contractorFormFields.forEach(field => {
      const fieldValue = this.data?.field_values?.find(fv => fv.id === field.id);
      const fieldType = field.componentType || field.type;

      if (fieldType === 'time') {
        const parsed = this.parseTimeValue(fieldValue?.value);
        this.form.addControl(field.name, new FormControl(parsed?.combined || null));
        this.form.addControl(`${field.name}_am_pm`, new FormControl(parsed?.amPm || null));
        this.form.addControl(`${field.name}_timePeriod`, new FormControl(parsed?.timePeriod || null));
      } else {
        this.form.addControl(field.name, new FormControl(fieldValue?.value ?? null));
      }
    });

    const initialValues = JSON.stringify(this.form.getRawValue());
    this.formSubscription = this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        const currentValues = JSON.stringify(this.form.getRawValue());
        if (currentValues !== initialValues) {
          this.projectsService.isAnyFormDirty.next(true);
          this.formSubscription?.unsubscribe();
        }
      });
  }

  private parseTimeValue(value: string): { combined: string; timePeriod: string; amPm: string } | null {
    if (!value) return null;
    const parts = value.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const amPm = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    const timePeriod = `${String(hours).padStart(2, '0')}:${minutes}`;
    return { combined: `${timePeriod} ${amPm}`, timePeriod, amPm };
  }

  private convertTo24h(timePeriod: string, amPm: string): string | null {
    if (!timePeriod || !amPm) return null;
    const parts = timePeriod.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    if (amPm === 'PM' && hours < 12) hours += 12;
    if (amPm === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}:00`;
  }

  validate(): boolean {
    const contractorValue = this.form.controls['sub_contractor']?.value;
    if (!contractorValue) {
      this.form.controls['sub_contractor'].setErrors({ required: true });
      this.form.controls['sub_contractor'].markAsTouched();
      this.errorMessages = { sub_contractor: 'Contractor is required' };
      return false;
    }
    this.errorMessages = {};
    return true;
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = JSON.parse(this.form.get('pictures')?.value || '[]');
      pictures = pictures.filter(o => o.id !== pic.id);
      this.form.get('pictures')?.setValue(JSON.stringify(pictures));
    } else if (pic.fileType === 'video') {
      let videos = JSON.parse(this.form.get('videos')?.value || '[]');
      videos = videos.filter(o => o.id !== pic.id);
      this.form.get('videos')?.setValue(JSON.stringify(videos));
    }
    this.mediaFiles = this.mediaFiles.filter(o => o.id !== pic.id);
  }

  save() {
    const values = this.form.getRawValue();

    const fieldValues = this.contractorFormFields
      .map(field => {
        const fieldType = field.componentType || field.type;
        let value = values[field.name];

        if (fieldType === 'time') {
          value = this.convertTo24h(values[`${field.name}_timePeriod`], values[`${field.name}_am_pm`]);
        } else if (fieldType === 'boolean') {
          value = value != null ? String(value) : null;
        }

        if (value == null) return null;

        return { id: field.id, value: String(value) };
      })
      .filter(Boolean);

    const payload: any = {
      ...this.data,
      sub_contractor_id: values.sub_contractor,
      field_values: fieldValues,
      pictures: JSON.parse(values.pictures || '[]'),
      videos: JSON.parse(values.videos || '[]'),
      comment: values.comment || null,
    };

    this.dailyReportService.save.next({
      type: 'sub_contractors',
      data: [payload],
    });
  }

  delete() {
    this.dailyReportService.delete.next({
      type: 'sub_contractors',
      id: this.data?.id,
    });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
